import { map, OperatorFunction } from 'rxjs';
import {
  HubspotPrettyTier,
  HubspotProductTierName,
  ProductName,
} from './rivet-product';

/** Any non-nested data that can be represented in JSON */
export type KeyValue = {
  key: string;
  value:
    | number
    | string
    | boolean
    | null
    | (number | string | boolean | null)[];
};

export type KeyValueForBigQuery = {
  key: string;
  value: string;
};

export type ClientMetadata = {
  user_agent: string;
  device_pixel_ratio: number;
  user_viewport_height: number;
  user_viewport_width: number;
  locales: readonly string[];
};

export type ParsedClientMetadata = ClientMetadata & {
  browser_family: string;
  browser_major_ver: string;
  browser_minor_ver: string;
  browser_patch_ver: string;

  os_family: string;
  os_major_ver: string;
  os_minor_ver: string;
  os_patch_ver: string;
};

export type EventRequest = {
  name: string;
  /** aka event properties */
  metadata?: KeyValue[];
  client_metadata?: ClientMetadata | ParsedClientMetadata;
  type?: EventTypes;
};

export type ServerEventRequest = EventRequest & {
  account_id: number;
};

export type EventTypes = 'user-event' | 'patient-event' | 'server-event';

export type SavedEvent = EventRequest & {
  session_id: string | null;
  account_id: number;
  user_id: number;
  timestamp: {
    value: string;
  };
};

export const navigationInfoKeys = [
  'current page name',
  'current page route',
  'current page url',
  'previous page name',
  'previous page route',
  'previous page url',
] as const;
export type NavigationInfoKey = (typeof navigationInfoKeys)[number];

export interface StorageClient {
  logUserEvent(userId: number, event: ServerEventRequest): Promise<void>;
  logPatientEvent(patientId: number, event: ServerEventRequest): Promise<void>;
  logSystemEvent(event: ServerEventRequest): Promise<void>;
}

export interface MixpanelEvent {
  distinct_id: number | string;
  account_id: number;
  cell: string;
  name: string;
  timestamp: Date;
  metadata?: KeyValue[];
  client_metadata?: ClientMetadata;
  environment: string;
}

export interface MixpanelUserEvent extends MixpanelEvent {
  user_id: number;
  email: string;
}

export interface MixpanelPatientEvent extends MixpanelEvent {
  patient_id: number;
}

export interface BigQueryClient extends StorageClient {
  getUserEvents(): Promise<SavedEvent[]>;
  getPatientEvents(): Promise<SavedEvent[]>;
}

export interface MixpanelClient extends StorageClient {
  updateUserProfile(
    user_id: number,
    update: MixpanelUserProperties,
  ): Promise<void>;
  updateUserProfilesBatch(updates: {
    [user_id: number]: MixpanelUserProperties;
  }): Promise<void>;
}

export type MixpanelUserProperties = {
  $email: string;
  $name: string;
  // Mapped to this in Lexicon: 'Is Riveteer': 'Yes' | 'No';
  type: 'system' | 'user';
  'Is admin in Rivet': 'Yes' | 'No';
  'Roles in Rivet': string[];
  'User Creation Date': string | null;
  'User Status': string | null;
  'Associated Company Name': string | null;
  'Associated Company Rivet ID': number | null;
  'HubSpot Owner': string | null;
} & {
  [K in `${ProductName} Tier`]: HubspotProductTierName | null;
} & {
  [K in `${ProductName} Purchased`]: number | null;
} & {
  [K in `${HubspotPrettyTier} - Implementation Complete Date`]: Date | null;
};

export function friendlierAnalyticsValue(value: any): any {
  if (value == null) return '(None)';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) {
    // Remap undefineds to nulls so they get transmitted over JSON.
    // (We don't pretty-print true/false values as Yes/No inside arrays).
    return value.map(item => (item === undefined ? null : item));
  }
  if (typeof value === 'object') {
    // Likely not useful for reports, but might be helpful for problem solving.
    return value;
  }
  return value;
}

export function addHidden<T extends {} | null>(
  properties: () => { [key: string]: any },
): OperatorFunction<T, T> {
  return map(item => {
    if (item === null) return null as T;
    if (typeof item !== 'object') {
      throw new Error(
        `addHidden: Unexpected non-object ${typeof item}: ${JSON.stringify(
          item,
        )}`,
      );
    }
    for (const [key, value] of Object.entries(properties())) {
      Object.defineProperty(item, key, {
        enumerable: false,
        value: value,
      });
    }
    return item;
  });
}
